import type React from 'react';

import type { Text } from '@zillow/constellation';
import { FieldSet, FormField, Input, Label, LabeledControl, Legend, Paragraph, Radio } from '@zillow/constellation';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { LANDSCAPING } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  customOption?: React.ReactNode;
};

const INITIAL_STATE = {
  [LANDSCAPING.FIELD_NAME.IS_ENABLED]: true,
  [LANDSCAPING.FIELD_NAME.SERVICES]: '',
  [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: false,
  [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: false,
};

export default function Landscaping_v1({ section, setSectionValues, customOption }: Props): JSX.Element {
  const landscaping_isEnabled = section.getFieldValue(LANDSCAPING.FIELD_NAME.IS_ENABLED);
  const option2Selected = section.getFieldValue(LANDSCAPING.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const option1Content = 'Responsible for reasonable upkeep including lawn mowing and leaf raking';
  const option2Content = 'Responsible for reasonable upkeep including lawn mowing and leaf raking';

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [LANDSCAPING.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(LANDSCAPING.FIELD_NAME.IS_ENABLED),
    });
  }

  function setServices(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.SERVICES]: event.currentTarget.value,
    });
  }

  function setVariableCostSelected(): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: false,
      [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: true,
    });
  }

  function setNoAdditionalFeeSelected(): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: true,
      [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: false,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <>
        <FormField
          marginY="sm"
          label={<Label>What services will be provided?</Label>}
          control={
            <Input
              placeholder="(example: Front lawn trimming, back yard weed removal)"
              value={section.getFieldValue(LANDSCAPING.FIELD_NAME.SERVICES)}
              onChange={setServices}
            />
          }
        />
        {section.getField(LANDSCAPING.FIELD_NAME.VARIABLE_COST) &&
          section.getField(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES) && (
            <FieldSet legend={<Legend>What is the additional fee for landscaping?</Legend>}>
              <LabeledControl
                controlId="LabeledControl-variable-monthly-cost"
                label={
                  <Label>
                    Variable monthly cost
                    <Paragraph>Landlord bills tenant for actual cost</Paragraph>
                  </Label>
                }
                size="lg"
                control={
                  <Radio
                    aria-label="Variable monthly cost"
                    name="additional-field"
                    checked={section.getFieldValue(LANDSCAPING.FIELD_NAME.VARIABLE_COST)}
                    onChange={setVariableCostSelected}
                  />
                }
                disabled={section.isFieldDisabled(LANDSCAPING.FIELD_NAME.VARIABLE_COST)}
              />
              <LabeledControl
                controlId="LabeledControl-no-additional-fee"
                label={<Label>No additional fee for landscaping</Label>}
                size="lg"
                control={
                  <Radio
                    aria-label="No additional fee for landscaping"
                    name="additional-field"
                    checked={section.getFieldValue(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES)}
                    onChange={setNoAdditionalFeeSelected}
                  />
                }
                disabled={section.isFieldDisabled(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES)}
              />
            </FieldSet>
          )}
      </>
    );
  }

  return (
    <>
      <HeaderToggle
        header="Landscaping"
        subheader="Applies to this property"
        switchOn={landscaping_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {landscaping_isEnabled && (
        <>
          <CenteredHR />
          {customOption ? (
            customOption
          ) : (
            <>
              <FormField
                label={<Label>Who will be responsible for landscaping?</Label>}
                control={
                  <RadioListBox
                    section={section}
                    setSectionValues={setSectionValues}
                    option1FieldName={LANDSCAPING.FIELD_NAME.TENANT_RESPONSIBILITY}
                    option2FieldName={LANDSCAPING.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                    option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                    option1Content={option1Content}
                    option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                    option2Content={option2Content}
                    initialState={INITIAL_STATE}
                  />
                }
              />
              {option2Selected && renderOption2Details()}
            </>
          )}
        </>
      )}
    </>
  );
}
